// src/components/chat/InviteFlowIncoming.jsx
// Incoming invite UI: accept/decline/block with optional decline note.
import React, { useState } from 'react';

export default function InviteFlowIncoming({
  hasAccepted,
  headerTitle,
  lastMessagePreview,
  declineNote,
  onDeclineNoteChange,
  onAccept,
  onDecline,
  onBlock,
  isAccepting,
  isDeclined,
  declinedDateLabel,
}) {
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  // Short "connecting" state after the invitee clicks Accept
  if (hasAccepted) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 rounded-full border-4 border-[#00aef7]/30 border-t-[#00aef7] animate-spin" />
          <p className="text-sm text-gray-500">
            Setting up your private chat…
          </p>
        </div>
      </div>
    );
  }

  // Main incoming invitee view
  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto overscroll-contain p-6 bg-gray-50">
        <div className="bg-green-50 border border-green-300 rounded-lg p-4 mb-4 text-sm text-green-800">
          <strong className="block text-base">
            You&apos;ve been invited to chat
          </strong>
          <p className="mt-1 text-gray-700">
            {headerTitle || 'This user'} would like to chat with you on eHandshake.
          </p>
        </div>

        {lastMessagePreview && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-800">
            <p className="font-semibold text-gray-700 mb-1">
              Message from {headerTitle || 'this user'}
            </p>
            <p className="mt-1 text-gray-700 whitespace-pre-line">
              {lastMessagePreview}
            </p>
          </div>
        )}

        {!isDeclined && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Optional note if declining
          </label>
          <textarea
            className="w-full min-h-[100px] border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#00aef7] bg-white"
            placeholder="Thanks, but I&apos;m not looking to join a team right now."
            value={declineNote}
            onChange={(e) => onDeclineNoteChange(e.target.value)}
          />
          <p className="mt-1 text-xs text-gray-400">
            If you click Decline, this note will be sent back with your response.
          </p>
        </div>
        )}
      </div>

      <div className="p-4 border-t border-gray-200 bg-white">
        {showBlockConfirm ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              Are you sure you want to block this user? They will never be
              able to send you a chat invitation again.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
                onClick={() => setShowBlockConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-1.5 rounded-md bg-red-600 text-white hover:bg-red-700 text-sm"
                onClick={() => {
                  setShowBlockConfirm(false);
                  onBlock?.();
                }}
              >
                Yes, Block
              </button>
            </div>
          </div>
        ) : isDeclined ? (
          <div className="flex justify-between items-center">
            <button
              type="button"
              className="px-3 py-1.5 rounded-md border border-red-300 text-red-600 hover:bg-red-50 text-sm"
              onClick={() => setShowBlockConfirm(true)}
            >
              Block
            </button>
            <div className="flex items-center gap-3">
              <span className="inline-block bg-gray-900 text-white text-xs px-3 py-1.5 rounded-md">
                {declinedDateLabel || 'Declined'}
              </span>
              <button
                type="button"
                className="px-3 py-1.5 rounded-md bg-[#00aef7] text-white hover:bg-[#009ad9] text-sm"
                onClick={onAccept}
              >
                Accept Invitation
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-center">
            <button
              type="button"
              className="px-3 py-1.5 rounded-md border border-red-300 text-red-600 hover:bg-red-50 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={() => setShowBlockConfirm(true)}
              disabled={isAccepting}
            >
              Block
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                className="px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={onDecline}
                disabled={isAccepting}
              >
                Decline
              </button>
              <button
                type="button"
                className="px-3 py-1.5 rounded-md bg-[#00aef7] text-white hover:bg-[#009ad9] text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={onAccept}
                disabled={isAccepting}
              >
                {isAccepting
                  ? 'Setting up your chat…'
                  : 'Accept Invitation'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
